import { useMemo, useRef, useState, useCallback, useLayoutEffect } from 'react';

/**
 * useChatDisplay
 * Shared hook to:
 *  - optionally filter messages (hide agent chain-of-thought / action chunks)
 *  - virtualize by limiting displayed count and offering incremental loading
 *  - maintain scroll position when prepending older messages
 *
 * @param {Array} messages - full message list
 * @param {boolean} showAllThoughts - if false, collapses consecutive assistant/action responses
 * @param {number} initialCount - initial number of latest messages to render
 */
export function useChatDisplay(messages, showAllThoughts = false, initialCount = 15, scrollContainerRef = null) {
  const [displayedCount, setDisplayedCount] = useState(initialCount);
  const scrollRestoreRef = useRef(null); // stores {heightBefore, scrollTop}
  const containerRef = useRef(null);
  const prevProcessedLenRef = useRef(0);
  const didInitPrevLenRef = useRef(false);

  const processed = useMemo(() => {
    if (!Array.isArray(messages) || messages.length === 0) return [];
    if (showAllThoughts) return messages;
    // Collapse logic: keep user/system messages and last assistant in a run
    const out = [];
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i];
      if (!m) continue;
      if (m.role === 'user' || m.role === 'system') {
        out.push(m);
        continue;
      }
      if (m.role === 'assistant') {
        const next = messages[i + 1];
        if (!next || next.role !== 'assistant') {
          out.push(m);
        } else {
          // skip intermediate assistant chunk
        }
      } else {
        out.push(m);
      }
    }
    return out;
  }, [messages, showAllThoughts]);

  const displayed = useMemo(() => processed.slice(-displayedCount), [processed, displayedCount]);

  const hasMore = processed.length > displayed.length;

  const getScrollEl = useCallback(() => {
    return (scrollContainerRef && scrollContainerRef.current) ? scrollContainerRef.current : containerRef.current;
  }, [scrollContainerRef]);

  const loadMore = useCallback(() => {
    const el = getScrollEl();
    if (el) {
      scrollRestoreRef.current = { heightBefore: el.scrollHeight, scrollTop: el.scrollTop };
    }
    setDisplayedCount(c => Math.min(c + 15, processed.length));
  }, [processed.length, getScrollEl]);

  useLayoutEffect(() => {
    const el = getScrollEl();
    if (!el) return;
    if (scrollRestoreRef.current) {
      const { heightBefore, scrollTop } = scrollRestoreRef.current;
      const delta = el.scrollHeight - heightBefore;
      el.scrollTop = scrollTop + delta;
      scrollRestoreRef.current = null;
    } else {
      const prevLen = prevProcessedLenRef.current || 0;
      const nextLen = processed.length || 0;
      const deltaLen = nextLen - prevLen;

      // Detect whether the user is anchored near the bottom *before* applying any auto-scroll behavior.
      const nearBottom = el.scrollHeight - el.clientHeight - el.scrollTop < 160;

      // IMPORTANT: When the user is NOT near bottom, keep the current window from sliding forward.
      // Otherwise, new messages cause the slice(-displayedCount) window to drop older rows and the
      // user's viewport appears to "auto-scroll" even though we didn't set scrollTop.
      if (deltaLen > 0 && !nearBottom) {
        const desired = Math.min(displayedCount + deltaLen, nextLen);
        if (desired !== displayedCount) {
          setDisplayedCount(desired);
        }
      } else if (nearBottom) {
        // Auto-scroll to bottom if user was near bottom
        el.scrollTop = el.scrollHeight;
      }

      prevProcessedLenRef.current = nextLen;
    }
  }, [displayed, processed.length, displayedCount, getScrollEl]);

  useLayoutEffect(() => {
    if (didInitPrevLenRef.current) return;
    didInitPrevLenRef.current = true;
    prevProcessedLenRef.current = processed.length || 0;
  }, [processed.length]);

  return { containerRef, processed, displayed, hasMore, loadMore, displayedCount };
}

export default useChatDisplay;
